/**
 * Push user_authenticated event to dataLayer for GA4 user-level tracking
 */
export const pushUserAuthenticated = (userId: string) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "user_authenticated",
    user_id: userId,
  });
};

/**
 * Push user_logged_out event to dataLayer to clear user tracking
 */
export const pushUserLoggedOut = () => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "user_logged_out",
    user_id: undefined,
  });
};

export type AccountType = "free" | "paid";
export type PaidTier = "starter" | "pro" | "unlimited" | "none";

/**
 * Get project count bucket from lifetime project count
 * Returns undefined for 0 projects (omit property)
 */
export const getProjectCountBucket = (count: number): string | undefined => {
  if (count === 1) return "1";
  if (count === 2 || count === 3) return "2-3";
  if (count >= 4) return "4+";
  return undefined; // omit when 0
};

/**
 * Push user_properties_updated event to dataLayer for GA4 user-level tracking
 */
export const pushUserProperties = (properties: {
  account_type?: AccountType;
  paid_tier?: PaidTier;
  is_activated?: boolean;
  activation_days_bucket?: string;
  project_count_bucket?: string;
}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "user_properties_updated",
    ...properties,
  });
};

/**
 * Derive account_type from subscription tier
 */
export const getAccountType = (tier: string): AccountType => {
  return tier === "free" ? "free" : "paid";
};

/**
 * Get paid_tier value - returns "none" for free users
 */
export const getPaidTier = (tier: string): PaidTier => {
  if (tier === "free") return "none";
  return tier as PaidTier;
};

/**
 * Push sign_up_completed event to dataLayer for GA4 acquisition tracking
 * This fires once per user after their first successful authentication
 */
export const pushSignUpCompleted = (params: {
  billing_cycle: "monthly" | "annual";
}) => {
  window.dataLayer = window.dataLayer || [];
  console.log('[Analytics] Pushing sign_up_completed event', params);
  window.dataLayer.push({
    event: "sign_up_completed",
    ...params,
  });
  console.log('[Analytics] dataLayer after push:', window.dataLayer);
};

/**
 * Push billing_cycle_toggled event to dataLayer for GA4 tracking
 * This fires only on user interaction with the billing cycle toggle, not on initial render
 */
export const pushBillingCycleToggled = (params: {
  from: "monthly" | "annual";
  to: "monthly" | "annual";
  location: "plans_page" | "auth_screen";
}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "billing_cycle_toggled",
    ...params,
  });
};

/**
 * Push project_created event to dataLayer for GA4 tracking
 * This fires once per project after successful creation
 */
export const pushProjectCreated = () => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "project_created",
  });
};

/**
 * Push email_version_generated event to dataLayer for GA4 tracking
 * This fires once per version after successful inbound email processing
 */
export const pushEmailVersionGenerated = (params: {
  project_id: string;
  version_number: number;
  source: string;
}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "email_version_generated",
    ...params,
  });
};

/**
 * Push project_share_link_copied event to dataLayer for GA4 tracking
 * This fires every time the user successfully copies a share link
 */
export const pushProjectShareLinkCopied = (params: {
  project_id: string;
  is_first_share_copy: boolean;
}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "project_share_link_copied",
    ...params,
  });
};

/**
 * Push project_activated event to dataLayer for GA4 tracking
 * This fires once per user lifetime when first stakeholder comment is received
 */
export const pushProjectActivated = () => {
  window.dataLayer = window.dataLayer || [];
  console.log('[Analytics] Pushing project_activated event');
  window.dataLayer.push({
    event: "project_activated",
    activation_source: "stakeholder_comment",
  });
  console.log('[Analytics] dataLayer after push:', window.dataLayer);
};

/**
 * Get comment length bucket for privacy-safe analytics
 */
export const getCommentLengthBucket = (length: number): string => {
  if (length <= 20) return "1-20";
  if (length <= 100) return "21-100";
  if (length <= 300) return "101-300";
  return "300+";
};

/**
 * Push stakeholder_comment_added event to dataLayer for GA4 tracking
 * This fires immediately after a stakeholder comment is successfully persisted
 */
export const pushStakeholderCommentAdded = (params: {
  project_id: string;
  version_id: string;
  comment_length_bucket: string;
}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "stakeholder_comment_added",
    ...params,
  });
};

/**
 * Push plan_selected event to dataLayer for GA4 tracking
 * This fires when an authenticated user actively selects a paid plan (intent signal)
 */
export const pushPlanSelected = (params: {
  selected_plan: "starter" | "pro" | "unlimited";
  billing_cycle: "monthly" | "annual";
  currency: string;
  displayed_price: number;
}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "plan_selected",
    ...params,
  });
};

/**
 * Push subscription_started event to dataLayer for GA4 tracking
 * This is a CONVERSION event - only fires after backend confirms subscription success
 * Deduplication handled via last_subscription_id_tracked in profiles table
 */
export const pushSubscriptionStarted = (params: {
  plan: "starter" | "pro" | "unlimited";
  billing_cycle: "monthly" | "annual";
  currency: string;
  value: number;
  subscription_id: string;
}) => {
  window.dataLayer = window.dataLayer || [];
  console.log('[Analytics] Pushing subscription_started event', params);
  window.dataLayer.push({
    event: "subscription_started",
    ...params,
  });
};

/**
 * Push subscription_canceled event to dataLayer for GA4 tracking
 * This fires after backend confirms subscription cancellation
 * Deduplication handled via last_cancellation_id_tracked in profiles table
 */
export const pushSubscriptionCanceled = (params: {
  plan: "starter" | "pro" | "unlimited";
  billing_cycle: "monthly" | "annual";
  cancellation_effective: "immediate" | "period_end";
  subscription_id: string;
  cancel_reason?: string;
  days_active?: number;
}) => {
  window.dataLayer = window.dataLayer || [];
  console.log('[Analytics] Pushing subscription_canceled event', params);
  window.dataLayer.push({
    event: "subscription_canceled",
    ...params,
  });
};